import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import Breadcrumb from '../components/Breadcrumb'
import { EffectivenessRatingDisplay } from '../components/EffectivenessRating'
import {
  Shield,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Plus,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Tag,
  Star,
  GitBranch,
  CheckSquare,
  Square,
  Trash2,
  UserPlus,
  Loader2,
  X,
  ScrollText,
  Paperclip,
} from 'lucide-react'

// Evidence type requirement icons and labels
const evidenceTypeConfig: Record<string, { label: string; icon: string; color: string; description: string }> = {
  evidence: { label: 'Evidence', icon: '📎', color: 'text-blue-600 bg-blue-50', description: 'Requires actual evidence (scans, screenshots, logs)' },
  policy: { label: 'Policy', icon: '📄', color: 'text-green-600 bg-green-50', description: 'Requires a linked policy document' },
  both: { label: 'Both', icon: '📄+📎', color: 'text-purple-600 bg-purple-50', description: 'Requires both policy and evidence' },
}

interface Control {
  id: string
  control_number: string
  name: string
  description?: string
  frequency: string
  // The ONE status field - computed based on evidence/policy compliance
  status: string
  // User override flag to mark as N/A
  is_not_applicable?: boolean
  status_reason?: string
  days_until_due?: number | null
  is_overdue?: boolean
  framework_name?: string
  owner_username?: string
  evidence_count?: number
  next_due_date?: number
  last_evidence_date?: number
  // Tags for categorization
  tags?: string[]
  // Effectiveness rating (1-5 scale)
  effectiveness_rating?: number | null
  // Issue #154: Shared service inheritance
  inherits_from_shared_service?: boolean
  inheritance_type?: 'full' | 'partial' | 'complementary' | null
  shared_service_count?: number
  // #311: Internal Control linkage
  internal_control_id?: string
  internal_control_number?: string
  internal_control_name?: string
  // #322: Internal control evidence and policy counts
  internal_control_evidence_count?: number
  internal_control_policy_count?: number
  // Evidence requirement type
  evidence_type?: 'evidence' | 'policy' | 'both'
}

interface Framework {
  id: string
  name: string
  display_name: string
}

interface UserOption {
  id: string
  username: string
}

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  compliant: { label: 'Compliant', color: 'badge-green', icon: CheckCircle },
  non_compliant: { label: 'Non-Compliant', color: 'badge-red', icon: XCircle },
  in_progress: { label: 'In Progress', color: 'badge-blue', icon: Clock },
  pending: { label: 'Pending', color: 'badge-yellow', icon: AlertTriangle },
  not_applicable: { label: 'N/A', color: 'badge-gray', icon: null },
}

export default function Controls() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const [controls, setControls] = useState<Control[]>([])
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '')
  const [frameworkFilter, setFrameworkFilter] = useState(
    searchParams.get('framework_id') || ''
  )
  const [ownerFilter, setOwnerFilter] = useState(searchParams.get('owner_id') || '')
  const [tagFilter, setTagFilter] = useState(searchParams.get('tag') || '')
  const [availableTags, setAvailableTags] = useState<string[]>([])
  // Auto-expand filters if any filter is pre-set from URL params
  const [showFilters, setShowFilters] = useState(
    !!(searchParams.get('status') || searchParams.get('framework_id') || searchParams.get('owner_id') || searchParams.get('tag'))
  )

  // Bulk selection state (#48)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkAction, setBulkAction] = useState<'assign_owner' | 'set_frequency' | 'mark_na' | 'delete' | null>(null)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [bulkOwner, setBulkOwner] = useState('')
  const [bulkFrequency, setBulkFrequency] = useState('')
  const [refreshCounter, setRefreshCounter] = useState(0)

  const limit = 20

  useEffect(() => {
    async function fetchFrameworks() {
      try {
        const res = await api.getFrameworks()
        if (res.success) {
          setFrameworks(res.frameworks)
        }
      } catch (err) {
        console.error('Failed to load frameworks:', err)
      }
    }
    fetchFrameworks()
  }, [])

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await api.getUsers()
        if (res.success) {
          setUsers(res.users)
        }
      } catch (err) {
        console.error('Failed to load users:', err)
      }
    }
    fetchUsers()
  }, [])

  useEffect(() => {
    async function fetchTags() {
      try {
        const res = await api.getControlTags()
        if (res.success) {
          setAvailableTags(res.tags)
        }
      } catch (err) {
        console.error('Failed to load tags:', err)
      }
    }
    fetchTags()
  }, [])

  useEffect(() => {
    async function fetchControls() {
      setLoading(true)
      try {
        const res = await api.getControls({
          limit,
          offset: page * limit,
          search: search || undefined,
          status: statusFilter || undefined,
          framework_id: frameworkFilter || undefined,
          owner_id: ownerFilter || undefined,
          tag: tagFilter || undefined,
        })
        if (res.success) {
          setControls(res.controls)
          setTotal(res.pagination.total)
        }
      } catch (err) {
        console.error('Failed to load controls:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchControls()
  }, [page, search, statusFilter, frameworkFilter, ownerFilter, tagFilter, refreshCounter])

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(0)
    const params = new URLSearchParams(searchParams)
    if (value) params.set('search', value)
    else params.delete('search')
    setSearchParams(params)
  }

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value)
    setPage(0)
    const params = new URLSearchParams(searchParams)
    if (value) params.set('status', value)
    else params.delete('status')
    setSearchParams(params)
  }

  const handleFrameworkFilter = (value: string) => {
    setFrameworkFilter(value)
    setPage(0)
    const params = new URLSearchParams(searchParams)
    if (value) params.set('framework_id', value)
    else params.delete('framework_id')
    setSearchParams(params)
  }

  const handleOwnerFilter = (value: string) => {
    setOwnerFilter(value)
    setPage(0)
    const params = new URLSearchParams(searchParams)
    if (value) params.set('owner_id', value)
    else params.delete('owner_id')
    setSearchParams(params)
  }

  const handleTagFilter = (value: string) => {
    setTagFilter(value)
    setPage(0)
    const params = new URLSearchParams(searchParams)
    if (value) params.set('tag', value)
    else params.delete('tag')
    setSearchParams(params)
  }

  const isOverdue = (control: Control) => {
    // Use backend-computed is_overdue if available
    if (control.is_overdue !== undefined) {
      return control.is_overdue
    }
    // Fallback to client-side calculation
    return (
      control.next_due_date &&
      control.next_due_date > 0 &&
      control.next_due_date < Date.now()
    )
  }

  // Bulk selection handlers (#48)
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === controls.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(controls.map(c => c.id)))
    }
  }

  const clearSelection = () => {
    setSelectedIds(new Set())
    setBulkAction(null)
    setShowBulkModal(false)
    setBulkOwner('')
    setBulkFrequency('')
  }

  const handleBulkAction = async () => {
    if (selectedIds.size === 0) return

    const ids = Array.from(selectedIds)
    setBulkLoading(true)

    try {
      if (bulkAction === 'assign_owner') {
        const result = await api.bulkUpdateControls(ids, { owner_id: bulkOwner || null })
        if (result.success) {
          toast.success(`Updated owner for ${result.updated_count} controls`)
          setRefreshCounter(c => c + 1)
          clearSelection()
        }
      } else if (bulkAction === 'set_frequency') {
        const result = await api.bulkUpdateControls(ids, { frequency: bulkFrequency })
        if (result.success) {
          toast.success(`Updated frequency for ${result.updated_count} controls`)
          setRefreshCounter(c => c + 1)
          clearSelection()
        }
      } else if (bulkAction === 'mark_na') {
        const confirmed = await confirm({
          title: 'Mark as Not Applicable',
          message: `Are you sure you want to mark ${ids.length} controls as Not Applicable?`,
          confirmText: 'Mark N/A',
        })
        if (confirmed) {
          const result = await api.bulkUpdateControls(ids, { is_not_applicable: true })
          if (result.success) {
            toast.success(`Marked ${result.updated_count} controls as N/A`)
            setRefreshCounter(c => c + 1)
            clearSelection()
          }
        }
      } else if (bulkAction === 'delete') {
        const confirmed = await confirm({
          title: 'Delete Controls',
          message: `Are you sure you want to delete ${ids.length} controls? This cannot be undone.`,
          confirmText: 'Delete',
          type: 'danger',
        })
        if (confirmed) {
          const result = await api.bulkDeleteControls(ids)
          if (result.success) {
            toast.success(`Deleted ${result.deleted_count} controls`)
            setRefreshCounter(c => c + 1)
            clearSelection()
          } else if (result.controls_with_evidence) {
            toast.error(`Cannot delete ${result.controls_with_evidence.length} controls with evidence`)
          }
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Bulk action failed')
    } finally {
      setBulkLoading(false)
      setShowBulkModal(false)
    }
  }

  return (
    <div className="space-y-6">
      <Breadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Controls</h1>
          <p className="text-gray-500">Manage compliance controls across frameworks</p>
        </div>
        <Link to="/controls/new" className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Control
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search controls..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            <ChevronDown
              className={`h-4 w-4 ml-2 transition-transform ${
                showFilters ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="label">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="input"
              >
                <option value="">All Statuses</option>
                <option value="compliant">Compliant</option>
                <option value="non_compliant">Non-Compliant</option>
                <option value="in_progress">In Progress</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
                <option value="not_applicable">Not Applicable</option>
              </select>
            </div>
            <div>
              <label className="label">Framework</label>
              <select
                value={frameworkFilter}
                onChange={(e) => handleFrameworkFilter(e.target.value)}
                className="input"
              >
                <option value="">All Frameworks</option>
                {frameworks.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Owner</label>
              <select
                value={ownerFilter}
                onChange={(e) => handleOwnerFilter(e.target.value)}
                className="input"
              >
                <option value="">All Owners</option>
                <option value="unassigned">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Tag</label>
              <select
                value={tagFilter}
                onChange={(e) => handleTagFilter(e.target.value)}
                className="input"
              >
                <option value="">All Tags</option>
                {availableTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Toolbar (#48) */}
      {selectedIds.size > 0 && (
        <div className="card p-4 bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-5 w-5 text-primary-600" />
              <span className="font-medium text-primary-900">
                {selectedIds.size} control{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setBulkAction('assign_owner')
                  setShowBulkModal(true)
                }}
                className="btn-secondary text-sm"
              >
                <UserPlus className="h-4 w-4 mr-1" />
                Assign Owner
              </button>
              <button
                onClick={() => {
                  setBulkAction('set_frequency')
                  setShowBulkModal(true)
                }}
                className="btn-secondary text-sm"
              >
                <Clock className="h-4 w-4 mr-1" />
                Set Frequency
              </button>
              <button
                onClick={() => {
                  setBulkAction('mark_na')
                  handleBulkAction()
                }}
                disabled={bulkLoading}
                className="btn-secondary text-sm"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Mark N/A
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => {
                    setBulkAction('delete')
                    handleBulkAction()
                  }}
                  disabled={bulkLoading}
                  className="btn-secondary text-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Controls List */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : controls.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No controls found</p>
            <Link to="/controls/new" className="btn-primary mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add your first control
            </Link>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <button
                        onClick={toggleSelectAll}
                        className="text-gray-400 hover:text-gray-600"
                        title={selectedIds.size === controls.length ? 'Deselect all' : 'Select all'}
                      >
                        {selectedIds.size === controls.length && controls.length > 0 ? (
                          <CheckSquare className="h-5 w-5 text-primary-600" />
                        ) : selectedIds.size > 0 ? (
                          <div className="h-5 w-5 border-2 border-primary-600 bg-primary-100 rounded flex items-center justify-center">
                            <div className="h-2 w-2 bg-primary-600 rounded-sm" />
                          </div>
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Control
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Framework
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Common Control
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="What's required to satisfy this control">
                      Requires
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Frequency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Due
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tags
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Effectiveness
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {controls.map((control) => {
                    // Status is now the single unified field
                    const status = statusConfig[control.status] || statusConfig.pending
                    const StatusIcon = status.icon

                    return (
                      <tr key={control.id} className={`hover:bg-gray-50 ${selectedIds.has(control.id) ? 'bg-primary-50' : ''}`}>
                        <td className="px-4 py-4 w-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleSelect(control.id)
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {selectedIds.has(control.id) ? (
                              <CheckSquare className="h-5 w-5 text-primary-600" />
                            ) : (
                              <Square className="h-5 w-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {control.control_number}
                                </span>
                                {/* Issue #154: Inheritance indicator */}
                                {control.inherits_from_shared_service && (
                                  <span
                                    className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${
                                      control.inheritance_type === 'full'
                                        ? 'bg-purple-100 text-purple-800'
                                        : control.inheritance_type === 'partial'
                                        ? 'bg-indigo-100 text-indigo-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}
                                    title={`${control.inheritance_type?.charAt(0).toUpperCase()}${control.inheritance_type?.slice(1)} inheritance from ${control.shared_service_count} shared service(s)`}
                                  >
                                    <GitBranch className="h-3 w-3 mr-0.5" />
                                    {control.inheritance_type === 'full'
                                      ? 'Inherited'
                                      : control.inheritance_type === 'partial'
                                      ? 'Partial'
                                      : 'Comp.'}
                                  </span>
                                )}
                                {isOverdue(control) && (
                                  <span className="badge-red text-xs">Overdue</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                                {control.name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">
                            {control.framework_name || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {control.internal_control_number ? (
                            <div className="flex flex-col">
                              <Link
                                to="/internal-controls"
                                className="text-sm text-primary-600 hover:text-primary-800 font-mono"
                                title={control.internal_control_name}
                              >
                                {control.internal_control_number}
                              </Link>
                              <span className="text-xs text-gray-500">
                                {control.internal_control_evidence_count || 0} ev, {control.internal_control_policy_count || 0} pol
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className={status.color} title={control.status_reason || ''}>
                              {StatusIcon && (
                                <StatusIcon className="h-3 w-3 mr-1 inline" />
                              )}
                              {status.label}
                            </span>
                            {control.days_until_due !== null && control.days_until_due !== undefined && control.days_until_due > 0 && control.days_until_due <= 14 && (
                              <span className="text-xs text-amber-600">
                                Due in {control.days_until_due} day{control.days_until_due !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {control.evidence_type && evidenceTypeConfig[control.evidence_type] ? (
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${evidenceTypeConfig[control.evidence_type].color}`}
                              title={evidenceTypeConfig[control.evidence_type].description}
                            >
                              {evidenceTypeConfig[control.evidence_type].icon}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700 capitalize">
                            {control.frequency}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-700">
                            <User className="h-4 w-4 mr-1 text-gray-400" />
                            {control.owner_username || 'Unassigned'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {control.last_evidence_date ? (
                            <span className="text-sm text-gray-700">
                              {new Date(control.last_evidence_date).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">Never</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {control.next_due_date ? (
                            <div className="flex flex-col">
                              <span className={`text-sm ${isOverdue(control) ? 'text-red-600 font-medium' : 'text-gray-700'}`}>
                                {new Date(control.next_due_date).toLocaleDateString()}
                              </span>
                              {isOverdue(control) && (
                                <span className="text-xs text-red-500">Overdue</span>
                              )}
                              {!isOverdue(control) && control.days_until_due !== null && control.days_until_due !== undefined && control.days_until_due <= 7 && (
                                <span className="text-xs text-amber-600">
                                  {control.days_until_due}d
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {control.tags && control.tags.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                              {control.tags.slice(0, 2).map((tag, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleTagFilter(tag)
                                    setShowFilters(true)
                                  }}
                                  className="inline-flex items-center px-1.5 py-0.5 text-xs rounded bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors cursor-pointer"
                                  title={`Filter by tag: ${tag}`}
                                >
                                  {tag}
                                </button>
                              ))}
                              {control.tags.length > 2 && (
                                <span className="text-xs text-gray-500">
                                  +{control.tags.length - 2}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {control.effectiveness_rating ? (
                            <EffectivenessRatingDisplay value={control.effectiveness_rating} size="sm" />
                          ) : (
                            <span className="text-gray-400 text-xs">Not rated</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <Link
                            to={`/controls/${control.id}`}
                            className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                          >
                            View
                            <ChevronRight className="h-4 w-4 inline ml-1" />
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of{' '}
                {total} controls
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * limit >= total}
                  className="btn-secondary disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bulk Action Modal (#48) */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowBulkModal(false)}
            />
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {bulkAction === 'assign_owner' && 'Assign Owner'}
                    {bulkAction === 'set_frequency' && 'Set Frequency'}
                  </h3>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Applying to {selectedIds.size} control{selectedIds.size !== 1 ? 's' : ''}
                </p>

                {bulkAction === 'assign_owner' && (
                  <div>
                    <label className="label">Select Owner</label>
                    <select
                      value={bulkOwner}
                      onChange={(e) => setBulkOwner(e.target.value)}
                      className="input"
                    >
                      <option value="">Unassigned</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.username}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {bulkAction === 'set_frequency' && (
                  <div>
                    <label className="label">Select Frequency</label>
                    <select
                      value={bulkFrequency}
                      onChange={(e) => setBulkFrequency(e.target.value)}
                      className="input"
                    >
                      <option value="">Select...</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semi-annually">Semi-Annually</option>
                      <option value="annually">Annually</option>
                      <option value="continuous">Continuous</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  onClick={handleBulkAction}
                  disabled={bulkLoading || (bulkAction === 'set_frequency' && !bulkFrequency)}
                  className="btn-primary w-full sm:ml-3 sm:w-auto disabled:opacity-50"
                >
                  {bulkLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Apply'
                  )}
                </button>
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="btn-secondary mt-3 w-full sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
